import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { useQuery } from 'react-query';
import { getLocationOrgIds } from '@frontend/api-location';
import {
  clearLastVisitedPage,
  getDecodedWeaveToken,
  getJTI,
  getLoginData,
  getUser,
  getWeaveToken,
  isWeaveTokenBufferActive,
  localStorageHelper,
  LOGIN_DATA_KEY,
  onWeaveTokenChange,
  PortalUser,
} from '@frontend/auth-helpers';
import { useBridgelineRedirect } from '@frontend/bridgeline-tendlc';
import { useRemoveAppLoader } from '@frontend/document';
import { useFetch } from '@frontend/fetch';
import { useGetDisplayNameStore } from '@frontend/get-display-name';
import { useSyncShellHistory } from '@frontend/history';
import { useTranslation } from '@frontend/i18n';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { useOnboardingRedirect } from '@frontend/onboarding';
import { useMerchants } from '@frontend/payments-hooks';
import { useInitializePhones } from '@frontend/phone';
import {
  useAppScopeStore,
  useAppScopeSetters,
  useAppScopeShellSync,
  useScopedAppFlagStore,
  parseSelectedScope,
} from '@frontend/scope';
import { useWorkspaceRouteGuard } from '@frontend/scope-access';
import { shell, useIpcShellProvider, useUserContextStore } from '@frontend/shell-utils';
import { useInitializePreferences, usePreference, usePreferenceStore } from '@frontend/user-preferences';
import { useWorkspaceFeature, useWorkspaceStore } from '@frontend/workspace-scope';
import { useAlert } from '@frontend/design-system';
import { signOut } from '../helpers/sign-out';
import { useApp } from '../providers/app.provider';
import {
  useApplyLocationSelectionSideEffects,
  useConfigureWeTags,
  useHandleScopeChange,
} from '../utils/configure-location';
import {
  createRequestAccessTokenRefreshStrategy,
  isDesktopBrowserAuthShell,
} from '../utils/request-access-token-refresh';
import { setCookie } from '../utils/set-cookie';
import {
  retryWithTrace,
  handle401Error,
  chaosMonkeyMiddleware,
  chaosMonkeyWithMemoryMiddleware,
} from './http-interceptors';
import { createRefreshTokenMiddleware, createResponseInterceptor401Middleware } from './http-middleware';
import { useProcessScopeDataFromOrgIds, determineInitialLocations } from './scope-init';
import { getShellThemeFromLocalStorage } from './shell-theme-switcher/helper';
import { useSyncExpandModeNudge } from './use-sync-expand-mode-nudge';

export type AppStateType =
  | {
      state: 'ONBOARDING';
      meta: {
        onboardingRedirectUrl: string;
      };
    }
  | {
      state: 'LOADING';
    }
  | {
      state: 'LOCATION_SELECTION';
    }
  | {
      state: 'INITIALIZED';
    }
  | {
      state: 'REDIRECTING';
    };

/**
 * For weave users, we can use the latestOrganizationId to determine the initial orgIds.
 * For non-weave users, we need to make a request to get the orgIds.
 */
const useInitialOrgIds = ({ user }: { user?: PortalUser }) => {
  const { data: orgIds = [] } = useQuery({
    queryKey: ['orgIds', user?.userID],
    queryFn: async () => getLocationOrgIds(),
    select: (data) => data.orgId,
    enabled: user?.type !== 'weave',
  });

  const loginData = user ? getLoginData(user.userID) : undefined;
  const weaveUserOrgId = loginData?.latestOrganizationId;
  const resolvedOrgIds = useMemo(
    () => (user?.type === 'weave' && weaveUserOrgId ? [weaveUserOrgId] : orgIds),
    [orgIds, weaveUserOrgId, user?.type]
  );

  return resolvedOrgIds;
};

export const useInitializeAppScopeStore = () => {
  useAppScopeShellSync();
  const { setLastUsedVertical } = useLastUsedVerticalShallowStore('setLastUsedVertical');
  const {
    setOrgData,
    setLocationData,
    setGroupData,
    setSelectedGroupId,
    setSelectedLocationIds,
    setSelectedOrgId,
    setSelectedScope,
    setScopeHierarchy,
  } = useAppScopeSetters();
  const user = getUser();
  const decodedWeaveToken = getDecodedWeaveToken();
  const userIsWeaveType = decodedWeaveToken?.type === 'weave';
  const keys = useMemo(() => Object.keys(decodedWeaveToken?.ACLS ?? {}), [decodedWeaveToken]);
  const { applyLocationSelectionSideEffects } = useApplyLocationSelectionSideEffects();
  const loginData = getLoginData(user?.userID ?? '');

  const [isComplete, setIsComplete] = useState(false);
  const orgIdsForLocationsQuery = useInitialOrgIds({ user });

  const { isSuccess, orgMap, locationMap, groupMap, locationHierarchy } = useProcessScopeDataFromOrgIds({
    orgIds: orgIdsForLocationsQuery,
    allowedLocationIds: userIsWeaveType ? undefined : keys,
  });

  const { latestLocationIds, lastOrgId } = useMemo(
    () =>
      determineInitialLocations({
        locationMap,
        groupMap,
      }),
    [locationMap, groupMap]
  );

  function setInitialValues() {
    const scopeHierarchy = Object.entries(locationHierarchy).reduce((acc, [groupId, locations]) => {
      const group = groupMap[groupId];

      if (!group.orgId) return acc;

      if (acc[group.orgId]) {
        acc[group.orgId][group.id] = locations;
      } else {
        acc[group.orgId] = {
          [group.id]: locations,
        };
      }

      return acc;
    }, {} as Record<string, Record<string, string[]>>);

    setGroupData(groupMap);
    setLocationData(locationMap);
    setOrgData(orgMap);
    setScopeHierarchy(scopeHierarchy);

    if (lastOrgId) {
      setSelectedOrgId(lastOrgId);
    }

    // Push orgId to shell relay store for persistence. Only set when we have
    // a non-empty value so the shell never overwrites a valid orgId with ''.
    const resolvedOrgId = lastOrgId ?? orgIdsForLocationsQuery?.[0];
    if (resolvedOrgId) {
      useUserContextStore.getState().setOrgId(resolvedOrgId);
    }

    if (
      latestLocationIds &&
      latestLocationIds.length > 0 &&
      (locationMap[latestLocationIds[0]] || groupMap[latestLocationIds[0]])
    ) {
      if ((locationMap[latestLocationIds[0]] || groupMap[latestLocationIds[0]]).vertical) {
        setLastUsedVertical((locationMap[latestLocationIds[0]] || groupMap[latestLocationIds[0]]).vertical as Vertical);
      }

      setSelectedLocationIds(latestLocationIds, { skipValidation: true });
      const groupId = locationMap[latestLocationIds[0]]?.groupId ?? groupMap[latestLocationIds[0]]?.id ?? '';
      setSelectedGroupId(groupId, { skipValidation: true });

      setSelectedScope(parseSelectedScope(loginData?.latestSelectedScope));

      const newLocationId = latestLocationIds[0];
      applyLocationSelectionSideEffects({
        newLocationId,
        onError: () => {
          const loginData: string | undefined = localStorageHelper.get(LOGIN_DATA_KEY);
          if (loginData) {
            localStorageHelper.delete(LOGIN_DATA_KEY);
            location.reload();
          }
        },
      });
    }
  }

  useEffect(() => {
    if (isSuccess && !isComplete) {
      setInitialValues();
      setIsComplete(true);
    }
  }, [isSuccess]);

  return {
    storeInitialized: isComplete,
  };
};

export const useHttpSetup = ({
  // appVersion,
  // shellVersion,
  token,
  jtiToken,
  isSessionTokenEnabled,
}: {
  appVersion?: string;
  shellVersion?: string;
  token?: string;
  jtiToken?: string;
  isSessionTokenEnabled: boolean;
}) => {
  const isSignOutTriggered = useRef(false);
  const { t } = useTranslation();
  const alerts = useAlert();
  const isSessionTokenEnabledRef = useRef(false);
  const [chaosBlockedURLS, setChaosBlockedURLS] = useState<string[]>([]);
  const [chaosAllowedURLS, setChaosAllowedURLS] = useState<string[]>([]);

  const {
    setAuthorizationHeader,
    addRequestInterceptor,
    removeRequestInterceptor,
    removeErrorInterceptor,
    addErrorInterceptor,
    addResponseInterceptor,
    removeResponseInterceptor,
    configureRetryer,
    // setHeader,
    // deleteHeader,
  } = useFetch();

  /**
   * TODO: this needs to wait until all services update monorail in order to respect these headers
   * If we push these now, we will get CORs errors
  useEffect(() => {
    if (shellVersion) {
      setHeader('X-Shell-Version', shellVersion);
    } else {
      deleteHeader('X-Shell-Version');
    }

    if (appVersion) {
      setHeader('X-App-Version', appVersion);
    } else {
      deleteHeader('X-App-Version');
    }
  }, [setHeader, deleteHeader, shellVersion, appVersion]);
    */

  const triggerInactiveSignOut = () => {
    if (!isSignOutTriggered.current) {
      alerts.error(t('You have been signed out due to inactivity. Please sign in again.'));
      isSignOutTriggered.current = true;
      setTimeout(() => {
        clearLastVisitedPage();
        signOut();
        isSignOutTriggered.current = false;
      }, 2000);
    }
  };

  // Use the extracted handler401Error function from http-interceptors.ts
  const handleError401 = handle401Error(triggerInactiveSignOut, isWeaveTokenBufferActive);

  const responseInterceptor401 = createResponseInterceptor401Middleware({
    setAuthorizationHeader,
  });

  useEffect(() => {
    const refreshTokenMiddleware = createRefreshTokenMiddleware({
      setAuthorizationHeader,
      alerts,
      t,
      triggerInactiveSignOut,
      refreshAccessToken: createRequestAccessTokenRefreshStrategy(),
    });

    addRequestInterceptor({ id: 'refresh-token', interceptor: refreshTokenMiddleware });
    if (localStorage.getItem('chaosMemory')) {
      addRequestInterceptor({
        id: 'chaos-monkey-memory',
        interceptor: chaosMonkeyMiddleware,
      });
    }
    if (localStorage.getItem('chaosMonkey')) {
      addRequestInterceptor({
        id: 'chaos-monkey-basic',
        interceptor: chaosMonkeyWithMemoryMiddleware(
          chaosBlockedURLS,
          chaosAllowedURLS,
          setChaosBlockedURLS,
          setChaosAllowedURLS
        ),
      });
    }
    const disableRetry = localStorage.getItem('disableHttpRetry') === 'true';
    configureRetryer(disableRetry ? undefined : retryWithTrace);
    setAuthorizationHeader(token ?? '', jtiToken ?? '');
    addErrorInterceptor({ id: '401', interceptor: handleError401 });
    const unsubscribeTokenChangeEvent = onWeaveTokenChange(
      (newToken: string | undefined, newJtiToken: string | undefined) => {
        setAuthorizationHeader(newToken ?? '', newJtiToken ?? '');
      }
    );
    // In Electron, the request middleware + main-process scheduler handle refresh
    // preemptively, so the 401 response interceptor is unnecessary and would bypass
    // the main process (which owns the refresh token).
    if (!isDesktopBrowserAuthShell()) {
      addResponseInterceptor({ id: '401', interceptor: responseInterceptor401 });
    }

    return () => {
      removeRequestInterceptor('chaos-monkey');
      removeRequestInterceptor('refresh-token');
      removeResponseInterceptor('401');
      removeErrorInterceptor('401');
      unsubscribeTokenChangeEvent();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    isSessionTokenEnabledRef.current = isSessionTokenEnabled;
  }, [isSessionTokenEnabled]);
};

export const useInitializeApp = (): AppStateType => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const { version: shellVersion } = useIpcShellProvider();
  const { currentVersion: appVersion } = useApp();
  const { selectedLocationIds, selectedGroupId } = useAppScopeStore();
  const isSessionTokenEnabled = getFeatureFlagValue('use-session-token');
  const weaveToken = getWeaveToken();
  const jtiToken = getJTI();

  useHttpSetup({
    token: weaveToken,
    jtiToken: jtiToken,
    isSessionTokenEnabled,
    appVersion: appVersion,
    shellVersion: shellVersion,
  });

  // This hook initializes user preferences and triggers the GET API call to fetch all user settings.
  useInitializePreferences();
  useRemoveAppLoader();

  // Auto-clear invalid workspace state when wetag is disabled.
  const { shouldClearWorkspace } = useWorkspaceFeature();
  const setSelectedWorkspaceId = useWorkspaceStore((state) => state.setSelectedWorkspaceId);
  const { setSelectedScope, setSelectedLocationIds, setSelectedGroupId } = useAppScopeSetters();

  useEffect(() => {
    if (shouldClearWorkspace) {
      console.warn('[Workspace Validator] Wetag disabled. Forcing location selection.');
      // Clear all workspace-related state
      setSelectedWorkspaceId(null);
      setSelectedScope(undefined);
      // Clear location selection to trigger fullscreen picker
      setSelectedLocationIds([]);
      setSelectedGroupId('');
    }
  }, [shouldClearWorkspace, setSelectedWorkspaceId, setSelectedScope, setSelectedLocationIds, setSelectedGroupId]);

  const locationId = selectedLocationIds[0] ?? selectedGroupId;
  const { storeInitialized } = useInitializeAppScopeStore();
  const { flagsInitialized } = useHandleScopeChange();
  const { weTagsInitialized } = useConfigureWeTags();
  const [ExistingUser] = usePreference('ExistingUser', { omitDefaultValue: true });
  const [isLegacyUser] = usePreference('has-used-legacy-desktop-client', { omitDefaultValue: true });
  const [showExpandModeNudge, setShowExpandModeNudge] = usePreference('showExpandModeNudge');

  const isDataInitialized = usePreferenceStore((state) => state.isDataInitialized);
  const isDataError = usePreferenceStore((state) => state.isDataError);
  useSyncShellHistory();
  useMerchants('selectedLocationIds');
  useInitializePhones({ ready: true });
  useWorkspaceRouteGuard();
  setCookie({ name: 'isWeaveCustomer', value: 'true' });
  const { shouldShowOnboardingLoader, onboardingRedirectUrl } = useOnboardingRedirect();
  const { bridgelineRedirectUrl, isBridgelineLoading } = useBridgelineRedirect();
  const activeRedirectUrl = onboardingRedirectUrl || bridgelineRedirectUrl;
  const setDisplayNamePref = useGetDisplayNameStore((state) => state.setDisplayNamePref);
  const showOnboardingFlow = shell?.isShell && shell?.featureAvailability?.has('onboarding-flow');

  useEffect(() => {
    const unsub = usePreferenceStore.subscribe(
      (state) => state?.prefs?.['displayNameControl'],
      (displayName) => {
        setDisplayNamePref(displayName?.value);
      }
    );

    return unsub;
  }, []);

  useSyncExpandModeNudge({
    showExpandModeNudge,
    setShowExpandModeNudge,
  });

  useLayoutEffect(() => {
    getShellThemeFromLocalStorage();
  }, []);

  useRemoveAppLoader();
  if (shouldShowOnboardingLoader || isBridgelineLoading || activeRedirectUrl) {
    return {
      state: shouldShowOnboardingLoader || isBridgelineLoading ? 'LOADING' : 'ONBOARDING',
      meta: {
        onboardingRedirectUrl: activeRedirectUrl,
      },
    };
  } else if (
    !storeInitialized ||
    !flagsInitialized ||
    !weTagsInitialized ||
    (showOnboardingFlow && !isDataInitialized && !isDataError)
  ) {
    return {
      state: 'LOADING',
    };
  } else if (
    !locationId ||
    (showOnboardingFlow && !ExistingUser && isDataInitialized && !isDataError && isLegacyUser)
  ) {
    return {
      state: 'LOCATION_SELECTION',
    };
  } else if (locationId && storeInitialized && flagsInitialized) {
    return {
      state: 'INITIALIZED',
    };
  }
  return {
    state: 'LOADING',
  };
};
